import React, { FC } from 'react'
import { Autocomplete, FormControl, Grid, InputLabel, TextField, Theme } from '@mui/material'
import { Controller, FieldValues, FieldPath } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import type { FireblocksSelectProps } from './FireblocksSelect.types'
import { SelectArrow } from '../SelectArrow'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: 300,
    height: 63,
    marginBottom: 25,
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: 300,
  },
  input: {
    height: 40,
    '& .MuiOutlinedInput-root': {
      paddingRight: 0,
    },
  },
  noOptions: {
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  loading: {
    color: theme.palette.primary.light,
  },
}))

export const FireblocksSelect = <T extends FieldValues, TName extends FieldPath<T>>({
  inputName,
  labelName,
  selectedId,
  placeholder,
  width,
  mb,
  noOptionsText,
  control,
  loading,
  options,
  required,
  rules,
}: FireblocksSelectProps<T, TName>): ReturnType<FC<FireblocksSelectProps<T, TName>>> => {
  // eslint-disable-next-line
  const classes = useStyles({} as any)
  const localLoading = Boolean(loading) && options.length === 0

  return (
    <Grid container className={classes.container} style={{ width, marginBottom: mb }}>
      <Grid item>
        <InputLabel shrink htmlFor={inputName} className={classes.label}>
          {labelName}
        </InputLabel>
      </Grid>
      <FormControl className={classes.formControl} style={{ width }}>
        <Controller
          name={inputName}
          control={control}
          // eslint-disable-next-line
          defaultValue={(selectedId as any) ?? null}
          rules={rules || (required ? { required: 'This field is required' } : undefined)}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={options}
              noOptionsText={noOptionsText}
              classes={{
                noOptions: classes.noOptions,
                loading: classes.loading,
              }}
              clearIcon={null}
              popupIcon={<SelectArrow />}
              getOptionLabel={(option) =>
                option.displayName && option.legacyId
                  ? `${option.displayName} (${option.legacyId})`
                  : option.label || String(option.value)
              }
              renderOption={(props, option) => (
                <li {...props} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {option.iconUrl ? (
                    <img
                      alt={`logo_${option.displayName}`}
                      src={option.iconUrl}
                      width={18}
                      height={18}
                      style={{ objectFit: 'contain' }}
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                      }}
                    />
                  ) : null}
                  <span>
                    {option.displayName && option.legacyId
                      ? `${option.displayName} (${option.legacyId})`
                      : option.label || String(option.value)}
                  </span>
                </li>
              )}
              loading={localLoading}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={(() => {
                const currentId = (field.value as string | null) ?? null
                return options.find((u) => u.value === currentId) || null
              })()}
              onChange={(_, newValue) => {
                if (newValue) {
                  field.onChange(newValue.value)
                } else {
                  field.onChange(null)
                }
              }}
              onBlur={field.onBlur}
              renderInput={(params) => {
                const currentId = (field.value as string | null) ?? null
                const selectedOption = options.find((u) => u.value === currentId) || null
                return (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={placeholder}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message as string}
                    InputProps={{
                      ...params.InputProps,
                      className: classes.input,
                      startAdornment: (
                        <>
                          {selectedOption?.iconUrl ? (
                            <img
                              alt={`logo_${selectedOption.displayName}`}
                              src={selectedOption.iconUrl}
                              width={18}
                              height={18}
                              style={{ objectFit: 'contain', margin: '0 6px 0 6px' }}
                              onError={(e) => {
                                ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                              }}
                            />
                          ) : null}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )
              }}
            />
          )}
        />
      </FormControl>
    </Grid>
  )
}
